import React, { useCallback, useEffect, useState } from 'react';
import { ConnectorRegistryEntry, ConnectorConnection, connectorsAPI } from '../../services/api';
import type { ConnectResult } from './ConnectorsModalProvider';
import { useConnectorConnect } from './useConnectorConnect';
import {
  fill,
  messageForCode,
  messageForStatus,
  realAccountLabel,
  resultLine,
  ACCOUNT_SWITCHED_COPY,
  DISCONNECT_CONFIRM,
  DISCONNECT_FAILED,
  LIVE_MODE_HELP,
  LIVE_MODE_LABEL,
  READ_ONLY_HELP,
  READ_ONLY_OFF_HELP,
  READ_ONLY_LABEL,
  READ_ONLY_SAVE_FAILED,
  READ_ONLY_TIMING,
  READ_ONLY_LOCKED_TEST_MODE,
  TEST_MODE_LABEL,
} from './copy';

/**
 * ByoConnectionBody — the manage panel body for a bring-your-own connector
 * (registry connectMode 'connect': the customer's own Stripe, Notion or
 * Linear). HeyGen keeps ManageConnectionConnectingState (Enable/Disable).
 *
 *   ┌─ not connected / disconnected ──────────────┐
 *   │  tagline                    [ Connect X ]    │
 *   ├─ connected ─────────────────────────────────┤
 *   │  Connected as Acme Inc  [Live mode]          │
 *   │  tagline                                     │
 *   │  Read-only                         (●──)     │
 *   │  …helper · Takes effect on your next message │
 *   │                              [ Disconnect ]  │
 *   ├─ needs_reauth ──────────────────────────────┤
 *   │  ⚠ copy                     [ Reconnect X ]  │
 *   └─────────────────────────────────────────────┘
 *
 * Status 'error' is still connected (the last call failed): the connected
 * body plus the copy for lastErrorCode. Any status this client does not know
 * renders the generic copy and Connect, never an empty body (DX-17).
 *
 * Visual tokens are the existing panel's (ManageConnectionPanel /
 * ManageConnectionConnectingState): same alert tones, pills and buttons.
 */
export interface ByoConnectionBodyProps {
  entry: ConnectorRegistryEntry;
  connection: ConnectorConnection | null;
  connectResult?: ConnectResult | null;
  onConnectionChange?: () => void | Promise<void>;
}

type BodyState = 'not_connected' | 'connected' | 'needs_reauth' | 'needs_setup' | 'unknown';

function bodyState(connection: ConnectorConnection | null): BodyState {
  if (!connection || connection.status === 'disconnected') return 'not_connected';
  if (connection.status === 'connected' || connection.status === 'error') return 'connected';
  if (connection.status === 'needs_reauth') return 'needs_reauth';
  if (connection.status === 'needs_setup') return 'needs_setup';
  return 'unknown';
}

const ALERT_TONE = {
  warn: 'bg-amber-500/10 text-amber-200 border-amber-500/30',
  error: 'bg-red-500/10 text-red-200 border-red-500/30',
  info: 'bg-white/5 text-white/80 border-white/10',
  success: 'bg-emerald-500/10 text-emerald-200 border-emerald-500/30',
};

const ByoConnectionBody: React.FC<ByoConnectionBodyProps> = ({
  entry,
  connection,
  connectResult,
  onConnectionChange,
}) => {
  const state = bodyState(connection);
  const name = entry.displayName;
  // GitHub connects on the spot: its result line comes from here, not from a
  // return URL.
  const [inPlace, setInPlace] = useState<ConnectResult | null>(null);
  const connector = useConnectorConnect({
    onConnected: async ({ connectorId, accountLabel, accountSwitched }) => {
      setInPlace({ connectorId, result: 'connected', reason: null, accountLabel, accountSwitched });
      if (onConnectionChange) await onConnectionChange();
    },
  });

  // Read-only: optimistic, reverted when the save fails.
  const [readOnly, setReadOnlyState] = useState<boolean>(!!(connection && connection.readOnly));
  const [savingReadOnly, setSavingReadOnly] = useState(false);
  const [readOnlyError, setReadOnlyError] = useState<string | null>(null);
  useEffect(() => {
    setReadOnlyState(!!(connection && connection.readOnly));
  }, [connection]);

  const [confirming, setConfirming] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);

  const toggleReadOnly = useCallback(async () => {
    const next = !readOnly;
    setReadOnlyState(next);
    setReadOnlyError(null);
    setSavingReadOnly(true);
    try {
      await connectorsAPI.setReadOnly(entry.id, next);
    } catch {
      setReadOnlyState(!next);
      setReadOnlyError(READ_ONLY_SAVE_FAILED);
    } finally {
      setSavingReadOnly(false);
    }
  }, [entry.id, readOnly]);

  const disconnect = useCallback(async () => {
    setDisconnecting(true);
    setDisconnectError(null);
    try {
      await connectorsAPI.disconnectOAuth(entry.id);
      setConfirming(false);
      setInPlace(null);
      if (onConnectionChange) await onConnectionChange();
    } catch {
      setDisconnectError(fill(DISCONNECT_FAILED, name));
    } finally {
      setDisconnecting(false);
    }
  }, [entry.id, name, onConnectionChange]);

  // The outcome of coming back from the service, for this connector only.
  const latest = inPlace || connectResult;
  const result = latest && latest.connectorId === entry.id ? latest : null;
  const resultText = result
    ? resultLine({
        result: result.result,
        serviceName: name,
        accountLabel:
          result.accountLabel || (result.result === 'connected' && connection ? connection.accountLabel : null),
        reason: result.reason,
      })
    : null;
  const resultTone = !result ? 'info' : result.result === 'connected' ? 'success' : result.result === 'cancelled' ? 'info' : 'warn';

  // The status line for anything but plain connected / not connected.
  let statusAlert: string | null = null;
  if (state === 'connected' && connection && connection.status === 'error') {
    statusAlert = messageForCode(connection.lastErrorCode || null, name);
  } else if (state === 'needs_reauth' || state === 'needs_setup' || state === 'unknown') {
    statusAlert = messageForStatus(connection && connection.status, name);
  }

  const accountLabel = realAccountLabel(connection && connection.accountLabel, entry.displayName);
  const mode = connection && connection.accountMode;
  // Review P0: Stripe stays read-only until the account is known to be test mode.
  const readOnlyLocked = !!entry.readOnlyUnlessTestMode && mode !== 'test';
  const readOnlyShown = readOnly || readOnlyLocked;
  // Just back from the service with no account name: the banner already says
  // "<name> is connected.", so the line below would repeat it word for word.
  const bodyLine = accountLabel ? `Connected as ${accountLabel}` : messageForStatus('connected', name);
  const showBodyLine = !(resultText && result && result.result === 'connected' && !accountLabel);
  const connectBlocked = connector.isBlocked(entry.id);
  const connectMessage = connector.messageFor(entry.id);
  const connectLabel =
    state === 'needs_reauth' ? `Reconnect ${name}` : `Connect ${name}`;

  return (
    <div className="px-6 sm:px-7 pb-7" data-testid="byo-connection-body">
      {resultText && (
        <div
          role="status"
          data-testid="byo-result"
          className={`mt-4 px-3 py-2 rounded-md text-sm border ${ALERT_TONE[resultTone]}`}
        >
          <div>{resultText}</div>
          {result && result.result === 'connected' && result.accountSwitched && (
            <div className="mt-1 text-xs opacity-80">{ACCOUNT_SWITCHED_COPY}</div>
          )}
        </div>
      )}

      {state === 'connected' && (
        <div className="pt-4">
          <div className="flex flex-wrap items-center gap-2">
            {showBodyLine && (
              <span className="text-sm font-medium text-white" data-testid="byo-account">
                {bodyLine}
              </span>
            )}
            {mode === 'live' && (
              <span
                data-testid="byo-mode-badge"
                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-200 border border-amber-500/30 text-xs font-semibold"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400" aria-hidden="true" />
                {LIVE_MODE_LABEL}
              </span>
            )}
            {mode === 'test' && (
              <span
                data-testid="byo-mode-badge"
                className="inline-flex items-center px-2 py-0.5 rounded-full bg-white/10 text-white/70 text-xs font-medium"
              >
                {TEST_MODE_LABEL}
              </span>
            )}
          </div>
          {mode === 'live' && <p className="mt-1 text-xs text-amber-200/80">{LIVE_MODE_HELP}</p>}
        </div>
      )}

      {statusAlert && (
        <div
          role="alert"
          data-testid="byo-status-alert"
          className={`mt-4 px-3 py-2 rounded-md text-sm border ${ALERT_TONE.warn}`}
        >
          {statusAlert}
        </div>
      )}

      <p className="text-sm text-white/80 leading-relaxed pt-4">{entry.tagline}</p>

      {state === 'connected' && (
        <div className="mt-6 border-t border-white/[0.08] pt-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div id={`byo-readonly-${entry.id}`} className="text-sm font-medium text-white">
                {READ_ONLY_LABEL}
              </div>
              <div className="text-xs text-white/55 mt-0.5">{readOnlyLocked ? READ_ONLY_LOCKED_TEST_MODE(entry.displayName) : readOnlyShown ? READ_ONLY_HELP : READ_ONLY_OFF_HELP}</div>
              <div className="text-xs text-white/40 mt-0.5">{READ_ONLY_TIMING}</div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={readOnlyShown}
              aria-labelledby={`byo-readonly-${entry.id}`}
              onClick={() => {
                void toggleReadOnly();
              }}
              disabled={savingReadOnly || readOnlyLocked}
              data-testid="byo-readonly-toggle"
              className={`relative inline-flex flex-shrink-0 items-center w-10 h-6 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-60 ${
                readOnlyShown ? 'bg-primary-600' : 'bg-white/15'
              }`}
            >
              <span
                aria-hidden="true"
                className={`inline-block w-4 h-4 rounded-full bg-white shadow transition-transform ${
                  readOnlyShown ? 'translate-x-5' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          {readOnlyError && (
            <div
              role="alert"
              className={`mt-3 px-3 py-2 rounded-md text-sm border ${ALERT_TONE.warn}`}
            >
              {readOnlyError}
            </div>
          )}
        </div>
      )}

      {disconnectError && (
        <div role="alert" className={`mt-5 px-3 py-2 rounded-md text-sm border ${ALERT_TONE.error}`}>
          {disconnectError}
        </div>
      )}

      {connectMessage && (
        <div role="alert" className={`mt-5 px-3 py-2 rounded-md text-sm border ${ALERT_TONE.warn}`}>
          {connectMessage}
        </div>
      )}

      <div className="mt-7 flex flex-wrap justify-end items-center gap-2">
        {state === 'connected' ? (
          !confirming ? (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              // Quiet until confirmed: the solid red "Yes, disconnect" below is the commitment.
              className="inline-flex items-center px-3 py-2 max-md:min-h-11 rounded-lg border border-red-500/40 text-red-300 hover:bg-red-500/10 hover:text-red-200 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-red-500"
              data-testid="byo-disconnect-start"
            >
              Disconnect
            </button>
          ) : (
            <>
              <p className="w-full text-sm text-white/75 text-right mb-1">{fill(DISCONNECT_CONFIRM, name)}</p>
              <button
                type="button"
                onClick={() => setConfirming(false)}
                disabled={disconnecting}
                className="inline-flex items-center px-3 py-2 max-md:min-h-11 rounded-lg border border-white/15 text-white/80 hover:bg-white/5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-white/30 disabled:opacity-60"
                data-testid="byo-disconnect-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  void disconnect();
                }}
                disabled={disconnecting}
                className="inline-flex items-center px-3 py-2 max-md:min-h-11 rounded-lg bg-red-600 hover:bg-red-500 text-white text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-60"
                data-testid="byo-disconnect-confirm"
              >
                {disconnecting ? 'Disconnecting…' : 'Yes, disconnect'}
              </button>
            </>
          )
        ) : (
          <>
            {entry.connectNote && (
              <p
                data-testid="byo-connect-note"
                className={`w-full px-3 py-2 rounded-md text-sm border ${ALERT_TONE.info}`}
              >
                {entry.connectNote}
              </p>
            )}
            <button
              type="button"
              onClick={() => {
                void connector.connect(entry);
              }}
              disabled={connectBlocked}
              data-testid="byo-connect"
              className="inline-flex items-center px-5 py-2.5 max-md:min-h-11 rounded-lg bg-primary-600 hover:bg-primary-500 text-white text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-60"
            >
              {connector.pendingId === entry.id ? 'Connecting…' : connectLabel}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ByoConnectionBody;
